<script>
// @ts-check

import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import ServiceLogo from '@/components/base/ServiceLogo.vue'
import { spotlight } from '@/constants/spotlight'
import { useLanguageStore } from '@/store/language'
import * as _ from 'lodash-es'
import { mapState } from 'pinia'
import Vuex from 'vuex'

export default {
  name: 'BaseViewVisitor',
  components: {
    LocaleSwitcher,
    ServiceLogo
  },
  computed: {
    ...mapState(useLanguageStore, ['language']),
    ...Vuex.mapGetters('app', ['isTlpService', 'isOyService']),
    ...Vuex.mapState('app', ['service']),
    spotlights() {
      if (this.isLoginRoute && !this.isTlpService) {
        return [spotlight.BUSINESS_ACCOUNT]
      }

      return [this.service]
    },
    spotlight() {
      return _.sample(this.spotlights)
    },
    translationPrefix() {
      if (this.isRegistrationRoute) {
        return 'registration'
      }
      return this.$route.name
    },
    isRegistrationRoute() {
      return this.$route.name === 'registration'
    },
    isLoginRoute() {
      return this.$route.name === 'login'
    },
    isAuthenticationRoute() {
      return this.isLoginRoute || this.isRegistrationRoute
    }
  },
  mounted() {
    if (this.isTlpService) {
      this.checkPromotions()
    }
  },
  methods: {
    ...Vuex.mapActions('promotionCodes', ['updatePromotionCode']),
    checkPromotions() {
      const promotionCode = this.$route.query.ukko_campaign
      if (promotionCode && typeof promotionCode === 'string') {
        this.updatePromotionCode(promotionCode.toLowerCase())
      }
    }
  }
}
</script>

<template>
  <div
    class="flex min-h-full flex-col overflow-hidden bg-white leading-normal md:flex-row"
    :class="{
      'h-screen': !isAuthenticationRoute,
      'flex-col-reverse': isAuthenticationRoute
    }"
  >
    <slot name="containers" />
    <div
      v-if="!isRegistrationRoute"
      class="absolute top-0 right-0 hidden pt-4 pr-4 md:block md:pr-8"
    >
      <LocaleSwitcher class="z-30 text-primary" />
    </div>
    <div
      class="left-col relative z-0 max-h-screen flex-col overflow-hidden md:w-1/2"
      :class="{
        'hidden justify-items-start bg-secondary-800 p-6 md:flex':
          !isAuthenticationRoute,
        'custom-background flex justify-center p-12': isAuthenticationRoute
      }"
    >
      <div
        class="flex max-h-screen flex-col"
        :class="{
          'items-center': isAuthenticationRoute
        }"
      >
        <div
          class="mb-6 flex"
          :class="{
            'justify-center': isAuthenticationRoute
          }"
        >
          <!-- Background image -->
          <img
            v-if="!isAuthenticationRoute"
            :src="require(`@/assets/images/${service}/login-screen.jpg`)"
            class="filter-50 absolute inset-0 z-0 size-full object-cover"
            alt=""
          />
          <ServiceLogo
            class="z-10 hidden md:flex"
            :white="!isAuthenticationRoute"
            plain-logo
            small
            link
          />
        </div>
        <div class="mb-16 flex">
          <div
            v-if="isAuthenticationRoute"
            class="spotlight-circle flex flex-col items-center justify-center rounded-full bg-white p-8"
          >
            <div class="spotlight-image mb-3 flex max-w-xs">
              <img
                class="max-h-56"
                :src="
                  require(
                    `@/assets/images/spotlight/${spotlight}-spotlight-${language}.png`
                  )
                "
                alt=""
              />
            </div>
            <div class="spotlight-text text-center">
              <h2 class="my-4 text-lg font-medium">
                {{ $t(`spotlight.${translationPrefix}.${spotlight}.title`) }}
              </h2>
              <p class="text-sm">
                {{
                  $t(`spotlight.${translationPrefix}.${spotlight}.description`)
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="right-col md:custom-shadow z-10 flex w-full justify-center overflow-y-auto p-4 md:h-screen md:w-1/2 md:border-l md:border-secondary-200 md:p-8 lg:px-12 xl:px-18"
    >
      <div class="flex w-full max-w-md flex-col">
        <div
          class="mb-6 flex w-full items-center justify-end"
          :class="isRegistrationRoute ? 'xl:ml-20' : 'md:hidden'"
        >
          <div v-if="isRegistrationRoute" class="inline-flex">
            <RouterLink
              :to="{ path: 'login' }"
              class="mr-4 text-sm font-medium text-primary"
            >
              {{ $t('registration.back_to_login') }}
            </RouterLink>
          </div>
          <div class="inline-flex">
            <LocaleSwitcher class="z-30 text-primary" />
          </div>
        </div>
        <div class="mb-6 flex w-full items-center justify-center md:hidden">
          <div class="mobile-service-logo-container inline-flex">
            <ServiceLogo plain-logo small class="text-primary" />
          </div>
        </div>
        <div class="w-full flex-1">
          <div class="content flex min-h-full w-full flex-col">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference "../styles/main.css";

.spotlight-circle {
  overflow: hidden;
  max-width: 545px;
  max-height: 545px;
  width: 545px;
  height: 545px;

  .spotlight-text {
    max-width: 300px;

    @variant xl {
      max-width: 360px;
    }
  }
}

.custom-background {
  background-color: var(--color-secondary-100);
}

.custom-shadow {
  box-shadow: -1px 0px 3px 0px rgba(0, 0, 0, 0.03);
}

/* iOS fix */
.mobile-service-logo-container {
  width: 12rem;
}

.content {
  & :deep(.text-input:not([disabled])) {
    @apply h-10 border-secondary-300 rounded-md px-3 py-0 text-sm text-secondary-800 placeholder:text-secondary-500 hover:border-primary hover:bg-white;
  }
}
</style>
